import { useEffect, useState } from "react";
import { Calendar, MessageSquare, User as UserIcon, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const STATUS_COLORS = {
  pending: "bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/30",
  confirmed: "bg-[#22D3EE]/15 text-[#22D3EE] border-[#22D3EE]/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data.bookings)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-6 md:px-10" data-testid="customer-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Customer dashboard</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-bold text-white">Welcome, {user?.name}</h1>
          <p className="text-[#94A3B8] mt-2">Track your consultations and AI assistant conversations.</p>
        </div>
        <a href="/#book" className="btn-primary">Book another call <ArrowRight size={14}/></a>
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        <StatCard icon={<Calendar size={18}/>} label="My bookings" value={bookings.length}/>
        <StatCard icon={<MessageSquare size={18}/>} label="Chats started" value={localStorage.getItem("thn_chat_session") ? 1 : 0}/>
        <StatCard icon={<UserIcon size={18}/>} label="Account" value={user?.role || ""}/>
      </div>

      <div className="mt-12">
        <h2 className="text-xl font-semibold text-white">Your bookings</h2>
        <div className="mt-4 card overflow-hidden">
          {loading ? (
            <div className="p-8 text-[#94A3B8]">Loading…</div>
          ) : bookings.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[#94A3B8]">No bookings yet.</p>
              <a href="/#book" className="btn-secondary mt-4 inline-flex">Book your first call</a>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[#64748B] uppercase text-[10px] tracking-[0.2em]">
                <tr>
                  <th className="text-left px-5 py-3">Service</th>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Time</th>
                  <th className="text-left px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`my-booking-${b.id}`}>
                    <td className="px-5 py-4 text-white">{b.service}</td>
                    <td className="px-5 py-4 text-[#94A3B8]">{b.preferred_date}</td>
                    <td className="px-5 py-4 text-[#94A3B8]">{b.preferred_time}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-1 rounded-md border text-xs uppercase tracking-[0.15em] ${STATUS_COLORS[b.status] || ''}`}>{b.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="card p-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] flex items-center justify-center">{icon}</div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#64748B]">{label}</div>
          <div className="text-2xl font-bold text-white font-mono">{value}</div>
        </div>
      </div>
    </div>
  );
}
